import * as React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { translate } from 'secullum-i18n';
import { Message, Card, RangeDatePicker } from 'secullum-react-native-ui';
import { Subscribe } from 'unstated';
import CardHorasPeriodo from '../../components/CardHorasPeriodo';
import CardIncluirIndicador from '../../components/CardIncluirIndicador';
import CardIndicadorFuncionario from '../../components/CardIndicadorFuncionario';
import CardSaldoBancoDeHoras from '../../components/CardSaldoBancoDeHoras';
import { FuncionarioContainer } from '../../containers/FuncionarioContainer';
import Api from '../../modules/api';
import { verificarExibirColuna } from '../../modules/perfilFuncionario';
import { IndicadoresDados, IndicadoresHoras } from '../../modules/types';

interface State {
  filtros: {
    dataInicial: Date;
    dataFinal: Date;
  };
  listaHoras: IndicadoresHoras;
  erro: string;
}

interface CardIndicadoresProps {
  onClickEditarIndicador: (idIndicador?: number) => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
}

const initialState: IndicadoresDados = {
  dia: { valores: [] },
  mes: { valores: [] },
  semana: { valores: [] }
};

const dataFinal = new Date();
const dataInicial = new Date(new Date().setDate(dataFinal.getDate() - 9));

class CardIndicadores extends React.Component<CardIndicadoresProps, State> {
  state: State = {
    filtros: {
      dataInicial: dataInicial,
      dataFinal: dataFinal
    },
    listaHoras: {
      extras: initialState,
      faltas: initialState,
      saldo: initialState,
      personalizados: []
    },
    erro: ''
  };

  async componentDidMount() {
    await this.listarHorasAsync();
  }

  listarHorasAsync = async () => {
    const { filtros } = this.state;

    const api = new Api();
    await api.getIndicadoresListaHoras(
      {
        dataInicio: filtros.dataInicial,
        dataFinal: filtros.dataFinal
      },
      {
        onSuccess: (listaHoras: IndicadoresHoras) => {
          this.setState({ listaHoras });
        },
        onError: (err: Error) => {
          const { onErroConexao } = this.props;

          if (err.name === 'TimeoutError' && onErroConexao) {
            onErroConexao(err.message, () => this.listarHorasAsync());
          } else {
            this.setState({ erro: err.message });
          }
        },
        onValidationError: errorList => {
          this.setState({ erro: errorList[0].message });
        }
      }
    );
  };

  handleDateChange = (value: Date, name: keyof State['filtros']) => {
    const novosFiltros = {
      ...this.state.filtros,
      [name]: value
    };

    if (name === 'dataInicial') {
      novosFiltros.dataFinal = value;
    }

    this.setState(
      {
        filtros: novosFiltros,
        erro: ''
      },
      async () => {
        if (name != 'dataFinal') {
          return;
        }

        if (this.state.filtros.dataInicial > this.state.filtros.dataFinal) {
          this.setState({
            erro: translate('Selecione o período corretamente')
          });
          return;
        }

        await this.listarHorasAsync();
      }
    );
  };

  render() {
    const { listaHoras, filtros, erro } = this.state;

    return (
      <View>
        <Card>
          <Card.Header title={translate('Filtrar')} />
          <Card.Section style={styles.section}>
            <RangeDatePicker
              nativeID="indicadores-data"
              style={styles.datePicker}
              label={translate('Insira o Período')}
              startDate={filtros.dataInicial}
              endDate={filtros.dataFinal}
              onStartDateChange={date => {
                this.handleDateChange(date, 'dataInicial');
              }}
              onEndDateChange={date => {
                this.handleDateChange(date, 'dataFinal');
              }}
            />
          </Card.Section>
        </Card>
        <View style={styles.cardIndicadores}>
          <Subscribe to={[FuncionarioContainer]}>
            {(funcionario: FuncionarioContainer) => {
              const { dadosPerfilFuncionario } = funcionario.state.dados;

              return (
                <>
                  {funcionario.state.dados.bancoHorasHabilitado &&
                    verificarExibirColuna(
                      'BSaldo',
                      dadosPerfilFuncionario.colunasOcultar
                    ) && (
                      <CardSaldoBancoDeHoras
                        nativeID="indicadores-banco-horas"
                        positivo
                        visualizacoes={listaHoras.saldo}
                      />
                    )}
                  {verificarExibirColuna(
                    'Extras',
                    dadosPerfilFuncionario.colunasOcultar
                  ) && (
                    <CardHorasPeriodo
                      nativeID="indicadores-horas-extras"
                      positivo
                      titulo={translate('Horas Extras')}
                      visualizacoes={listaHoras.extras}
                    />
                  )}
                  {verificarExibirColuna(
                    'Faltas',
                    dadosPerfilFuncionario.colunasOcultar
                  ) && (
                    <CardHorasPeriodo
                      nativeID="indicadores-horas-faltantes"
                      positivo={false}
                      titulo={translate('Horas Faltantes')}
                      visualizacoes={listaHoras.faltas}
                    />
                  )}
                </>
              );
            }}
          </Subscribe>
          {(listaHoras.personalizados || []).map((dados, indice) => (
            <CardIndicadorFuncionario
              key={`indicadores-funcionario-${indice}`}
              nativeID={`indicadores-funcionario-${indice}`}
              nomeColunaIndicador={dados.nomeColunaIndicador}
              titulo={dados.indicador.titulo}
              visualizacoes={dados.dadosIndicador}
              corTexto={dados.indicador.corTexto}
              indicadorId={dados.indicador.id}
              onEditarIndicador={() =>
                dados.indicador.id &&
                this.props.onClickEditarIndicador(dados.indicador.id)
              }
              onErroConexao={this.props.onErroConexao}
            />
          ))}
          <Message
            nativeID="indicadores-erro"
            type="warning"
            message={erro}
            visible={erro !== ''}
            onRequestClose={() => this.setState({ erro: '' })}
          />
          <CardIncluirIndicador
            onClickIncluirIndicador={this.props.onClickEditarIndicador}
            customContainerStyles={styles.cardIncluirIndicador}
            cardHeight={290}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  cardIndicadores: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    flexWrap: 'wrap',
    overflow: 'hidden',
    padding: Platform.OS === 'web' ? 16 : undefined,
    marginTop: Platform.OS === 'web' ? -20 : undefined,
    marginLeft: Platform.OS === 'web' ? -16 : undefined
  },
  cardIncluirIndicador: {
    marginTop: 16
  },
  section: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingLeft: 0
  },
  datePicker: {
    flex: 1,
    minWidth: 250,
    maxWidth: 350,
    marginLeft: 16
  }
});

export default CardIndicadores;
